import { create } from 'zustand';
import { bookingServicesApi } from '../../../api/api';
import type {
  BookingServiceResponse,
  CreateBookingServiceRequest,
  UpdateBookingServiceRequest,
  DuplicateServiceRequest,
  BulkUpdateServicesRequest,
} from '../../../api/generated';

type ServiceState = {
  services: BookingServiceResponse[];
  loading: boolean;
  error: string | null;

  // Actions
  loadServices: (filters?: {
    page?: number;
    limit?: number;
    q?: string;
    category?: string;
    active?: boolean;
    staff?: string;
    minPrice?: number;
    maxPrice?: number;
    minDuration?: number;
    maxDuration?: number;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => Promise<void>;

  createService: (serviceData: CreateBookingServiceRequest) => Promise<BookingServiceResponse>;
  updateService: (id: string, serviceData: UpdateBookingServiceRequest) => Promise<BookingServiceResponse>;
  deleteService: (id: string) => Promise<void>;
  duplicateService: (duplicateData: DuplicateServiceRequest) => Promise<BookingServiceResponse>;
  bulkUpdateServices: (bulkData: BulkUpdateServicesRequest) => Promise<BookingServiceResponse[]>;
  bulkDeleteServices: (serviceIds: string[]) => Promise<void>;

  clearError: () => void;
};

export const useServiceStore = create<ServiceState>(set => ({
  services: [],
  loading: false,
  error: null,

  loadServices: async (filters = {}) => {
    set({ loading: true, error: null });

    try {
      const response = await bookingServicesApi.getServices({
        page: filters.page || 1,
        limit: filters.limit || 100,
        q: filters.q,
        category: filters.category,
        active: filters.active,
        staff: filters.staff,
        minPrice: filters.minPrice,
        maxPrice: filters.maxPrice,
        minDuration: filters.minDuration,
        maxDuration: filters.maxDuration,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder,
      });

      // API nyní vrací typovou odpověď PaginatedResponseBookingServiceResponse
      const services = response.data.data;
      set({ services, loading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při načítání služeb',
        loading: false,
      });
    }
  },

  createService: async serviceData => {
    set({ loading: true, error: null });

    try {
      const response = await bookingServicesApi.createService({
        createBookingServiceRequest: serviceData,
      });

      const newService = response.data;
      set(state => ({
        services: [...state.services, newService],
        loading: false,
      }));

      return newService;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při vytváření služby',
        loading: false,
      });
      throw error;
    }
  },

  updateService: async (id, serviceData) => {
    set({ loading: true, error: null });

    try {
      console.log('📤 Store: Odesílám updateService s daty:', { id, serviceData });
      const response = await bookingServicesApi.updateService({
        id,
        updateBookingServiceRequest: serviceData,
      });

      const updatedService = response.data;
      set(state => ({
        services: state.services.map(s => (s.id === id ? updatedService : s)),
        loading: false,
      }));

      return updatedService;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při úpravě služby',
        loading: false,
      });
      throw error;
    }
  },

  deleteService: async id => {
    set({ loading: true, error: null });

    try {
      await bookingServicesApi.deleteService({ id });

      set(state => ({
        services: state.services.filter(s => s.id !== id),
        loading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při mazání služby',
        loading: false,
      });
      throw error;
    }
  },

  duplicateService: async duplicateData => {
    set({ loading: true, error: null });

    try {
      const response = await bookingServicesApi.duplicateService({
        duplicateServiceRequest: duplicateData,
      });

      const duplicatedService = response.data;
      set(state => ({
        services: [...state.services, duplicatedService],
        loading: false,
      }));

      return duplicatedService;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při duplikaci služby',
        loading: false,
      });
      throw error;
    }
  },

  bulkUpdateServices: async bulkData => {
    set({ loading: true, error: null });

    try {
      const response = await bookingServicesApi.bulkUpdateServices({
        bulkUpdateServicesRequest: bulkData,
      });

      const updatedServices = response.data;
      set(state => ({
        services: state.services.map(service => {
          const updated = updatedServices.find(us => us.id === service.id);
          return updated || service;
        }),
        loading: false,
      }));

      return updatedServices;
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při hromadné úpravě služeb',
        loading: false,
      });
      throw error;
    }
  },

  bulkDeleteServices: async serviceIds => {
    set({ loading: true, error: null });

    try {
      await bookingServicesApi.bulkDeleteServices({
        requestBody: serviceIds,
      });

      set(state => ({
        services: state.services.filter(s => !serviceIds.includes(s.id)),
        loading: false,
      }));
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Chyba při hromadném mazání služeb',
        loading: false,
      });
      throw error;
    }
  },

  clearError: () => {
    set({ error: null });
  },
}));
